/**
 * The body of the preview pane: a read-only rendering of one document version.
 * PDFs render page by page (pdf.js); Word documents show their text with
 * headings; filed emails show their headers and plain-text body. Anything
 * else offers the download.
 *
 * @module components/apps/imanage/IManageDocumentPreview
 */
import type { IManageDocument } from "@amoreg/contracts/imanage";
import { FileQuestionIcon } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";

import { cn } from "~/lib/utils";
import { base64ToBytes } from "../../../state/imanage";
import { Skeleton } from "../../ui/skeleton";
import { fileKind } from "./imanageFormat";
import { docxPreview, parseEmail, type EmailPreview, type PreviewParagraph } from "./imanagePreview";
import { InlineError } from "./IManageParts";

const PdfAnnotationViewer = lazy(() => import("../../files/PdfAnnotationViewer"));

export function PreviewSkeleton() {
  return (
    <div className="flex flex-col gap-2.5 p-5" aria-busy="true" aria-label="Loading preview">
      <Skeleton className="mb-2 h-5 w-2/3" />
      {Array.from({ length: 12 }, (_, i) => (
        <Skeleton key={i} className="h-3" style={{ width: `${92 - ((i * 13) % 35)}%` }} />
      ))}
    </div>
  );
}

export function IManageDocumentPreview({ document }: { readonly document: IManageDocument }) {
  const kind = fileKind(document);
  if (kind === "pdf") return <PdfPreview document={document} />;
  if (kind === "word" && document.extension === "docx") return <WordPreview document={document} />;
  if (kind === "email" && document.extension === "eml") return <EmailBody document={document} />;
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center text-sm text-muted-foreground">
      <FileQuestionIcon aria-hidden className="size-6" />
      No preview for this file type. Download it to open it in its own application.
    </div>
  );
}

function PdfPreview({ document }: { readonly document: IManageDocument }) {
  // The object URL lives exactly as long as this effect (StrictMode re-runs it).
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const next = URL.createObjectURL(
      new Blob([base64ToBytes(document.content).slice()], { type: "application/pdf" }),
    );
    setUrl(next);
    return () => {
      setUrl(null);
      URL.revokeObjectURL(next);
    };
  }, [document.content]);
  return (
    <div className="flex min-h-[480px] flex-1 flex-col" data-imanage-preview="pdf">
      {url ? (
        <Suspense fallback={<PreviewSkeleton />}>
          <PdfAnnotationViewer url={url} filePath={document.name} />
        </Suspense>
      ) : (
        <PreviewSkeleton />
      )}
    </div>
  );
}

function WordPreview({ document }: { readonly document: IManageDocument }) {
  const [state, setState] = useState<{
    content: string;
    paragraphs?: PreviewParagraph[];
    error?: string;
  } | null>(null);
  useEffect(() => {
    let active = true;
    docxPreview(base64ToBytes(document.content)).then(
      (paragraphs) => active && setState({ content: document.content, paragraphs }),
      (e: unknown) =>
        active &&
        setState({
          content: document.content,
          error: e instanceof Error ? e.message : "This Word document could not be read.",
        }),
    );
    return () => {
      active = false;
    };
  }, [document.content]);
  if (!state || state.content !== document.content) return <PreviewSkeleton />;
  if (state.error) return <InlineError className="m-5" message={state.error} />;
  return (
    <article
      className="mx-auto w-full max-w-[680px] px-6 py-6 text-[13.5px] leading-relaxed text-foreground"
      data-imanage-preview="word"
    >
      {state.paragraphs!.map((p, i) =>
        p.role === "title" ? (
          <h2 key={i} className="mb-3 text-lg font-semibold leading-snug tracking-tight">
            {p.text}
          </h2>
        ) : p.role === "heading" ? (
          <h3 key={i} className="mt-5 mb-1.5 text-[13.5px] font-semibold">
            {p.text}
          </h3>
        ) : (
          <p key={i} className={cn("mb-2.5 whitespace-pre-wrap", !p.text.trim() && "h-2")}>
            {p.text}
          </p>
        ),
      )}
    </article>
  );
}

function EmailBody({ document }: { readonly document: IManageDocument }) {
  const email: EmailPreview | null = useMemo(() => {
    try {
      return parseEmail(new TextDecoder().decode(base64ToBytes(document.content)));
    } catch {
      return null;
    }
  }, [document.content]);
  if (!email) return <InlineError className="m-5" message="This email could not be read." />;
  const rows: Array<[string, string | undefined]> = [
    ["From", email.from],
    ["To", email.to],
    ["Cc", email.cc],
    ["Date", email.date],
  ];
  return (
    <article className="flex flex-col" data-imanage-preview="email">
      <header className="border-b border-border/60 px-5 py-4">
        <h2 className="text-[15px] font-semibold leading-snug">{email.subject ?? document.name}</h2>
        <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[13px]">
          {rows
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="min-w-0 break-words">{value}</dd>
              </div>
            ))}
        </dl>
      </header>
      <div className="px-5 py-4 text-[13.5px] leading-relaxed whitespace-pre-wrap">{email.body}</div>
    </article>
  );
}
